import React, { FC, useEffect } from "react";

// Components
import { StepLayout } from "./StepLayout/StepLayout";
import { TreatmentsCardList } from "../FormElements/CardList/TreatmentsCardList";
import { InformationCardWithFiltersReset } from "../InformationCard/InformationCardWithFiltersReset";

// Helpers
import { RootDataType } from "../Helpers/interfaces";
import { setStorage } from "../Helpers/formFunctions";
import { stepHeadings } from "../stepHeadings";
import {
	findClosestParentContainerWithSpecializationName,
	findTreatmentById,
} from "../Helpers/treatmentsHelpers";
import {
	isServiceContainer,
	isTreatmentContainer,
} from "../Helpers/treatmentsHelpers";
import { parentTreatmentInitialState } from "../Helpers/initialState/parentTreatmentInitialState";
import { doctorInitialState } from "../Helpers/initialState/doctorInitialState";
import { locationInitialState } from "../Helpers/initialState/locationInitialState";
import { GtmService } from "../GtmService";

// Types & Models
import {
	FormDispatch,
	FormFields,
	FormReducer,
	FormStateType,
} from "../Helpers/formTypes";
import { ServiceContainerDto } from "@apiModels/ServiceContainerDto.csharp";
import { TreatmentsContainerDto } from "@apiModels/TreatmentsContainerDto.csharp";

// Hooks
import { useLabels } from "../Hooks/useLabels";
import { useFilteredTreatment } from "../Hooks/useFilteredTreatment";

export interface TreatmentStepType {
	state: FormStateType;
	dispatch: FormDispatch;
	nextStep: () => void;
	rootData: RootDataType;
}

export const TreatmentStep: FC<TreatmentStepType> = ({
	state,
	dispatch,
	rootData,
	nextStep,
}) => {
	const { bookingLabels } = useLabels();

	const filteredTreatment = useFilteredTreatment(
		state.treatment as TreatmentsContainerDto,
		state.location,
		state.doctor
	);

	useEffect(() => {
		if (!filteredTreatment) return;

		if (filteredTreatment.parentContainerId) {
			const parentTreatment = findTreatmentById(
				rootData.treatments,
				filteredTreatment.parentContainerId
			);
			dispatch({
				type: FormReducer.update,
				field: FormFields.parentTreatment,
				payload: parentTreatment,
			});
		} else {
			dispatch({
				type: FormReducer.update,
				field: FormFields.parentTreatment,
				payload: parentTreatmentInitialState,
			});
		}
	}, [filteredTreatment, rootData.treatments]);

	const onTreatmentSelect = (
		treatment: TreatmentsContainerDto | ServiceContainerDto
	) => {
		const rootTreatment = findTreatmentById(
			rootData.treatments,
			treatment.containerId
		) || treatment;
		if (isTreatmentContainer(rootTreatment)) {
			const treatmentContainer = rootTreatment;
			dispatch({
				type: FormReducer.update,
				field: FormFields.treatment,
				payload: treatmentContainer,
			});

			GtmService.treatmentStepSubmit(
				state.specializationName,
				state.service.specializationId,
				state.service.bookingName,
				state.service.id,
				state.location.name,
				state.location.id,
				state.doctor.name,
				state.doctor.id
			);
		} else if (isServiceContainer(rootTreatment)) {
			const serviceContainer = rootTreatment;
			const specializationName =
				findClosestParentContainerWithSpecializationName(
					serviceContainer,
					rootData.treatments
				);

			dispatch({
				type: FormReducer.update,
				field: FormFields.service,
				payload: serviceContainer,
			});
			dispatch({
				type: FormReducer.update,
				field: FormFields.treatment,
				payload: serviceContainer,
			});
			dispatch({
				type: FormReducer.update,
				field: FormFields.parentTreatment,
				payload: filteredTreatment,
			});
			dispatch({
				type: FormReducer.update,
				field: FormFields.specializationName,
				payload: specializationName,
			});

			setStorage(state);
			nextStep();

			GtmService.treatmentStepSubmit(
				state.specializationName,
				serviceContainer.specializationId,
				serviceContainer.bookingName,
				serviceContainer.id,
				state.location.name,
				state.location.id,
				state.doctor.name,
				state.doctor.id
			);
		}
	};

	const isDoctorSelected = state.doctor.id && state.doctor.id != "-1";
	const isLocationSelected = state.location.id && state.location.id != "-1";

	const handleResetFilters = () => {
		if (isDoctorSelected) {
			dispatch({
				type: FormReducer.update,
				field: FormFields.doctor,
				payload: doctorInitialState,
			});
		}
		if (isLocationSelected) {
			dispatch({
				type: FormReducer.update,
				field: FormFields.location,
				payload: locationInitialState,
			});
		}
		setStorage(state);
	};

	const currentTreatmentCustomStepHeading = filteredTreatment.stepHeading;
	const treatmentStepCustomHeading =
		bookingLabels?.stepsBookingLabels?.newBookingStepsLabels
			.treatmentStepHeading;
	const treatmentStepHeading =
		currentTreatmentCustomStepHeading ||
		treatmentStepCustomHeading ||
		stepHeadings.treatmentStepHeading;

	return (
		<div className="TreatmentGroupStep">
			<StepLayout heading={treatmentStepHeading}>
				{(isDoctorSelected || isLocationSelected) && (
					<InformationCardWithFiltersReset
						doctor={state.doctor}
						location={state.location}
						onResetFilters={handleResetFilters}
					/>
				)}
				<div className="TreatmentGroupStep__selectContainer">
					<TreatmentsCardList
						treatment={filteredTreatment}
						onSelect={onTreatmentSelect}
					/>
				</div>
			</StepLayout>
		</div>
	);
};
